import { z } from 'zod'

/**
 * Auth contract of the backend API v1 (OpenAPI: LoginIn, LoginOut, TokenOut, RefreshIn, UserOut, ErrorOut).
 *   POST /auth/login    → tokens + user | 401 invalid_credentials    body: { email, password }
 *   POST /auth/refresh  → TokenPair     | 401 invalid_refresh_token   body: { refresh_token }, works once
 *   POST /auth/logout   → 204                                         body: { refresh_token }
 *   GET  /auth/me       → AuthUser      | 401 not_authenticated       header: Authorization: Bearer
 * Errors are `{ code, message }`; `code` is stable (the three values above) and the same in every language.
 * Field names are the backend's (snake_case); the frontend session model lives in src/shared/auth.
 */
export const authUserSchema = z.object({
  /** UUID; opaque to the frontend. */
  id: z.string().min(1),
  email: z.string().min(1),
  first_name: z.string(),
  last_name: z.string(),
})
export type AuthUser = z.infer<typeof authUserSchema>

export const tokenPairSchema = z.object({
  /** Opaque; sent as `Authorization: Bearer`. */
  access_token: z.string().min(1),
  token_type: z.literal('bearer').default('bearer'),
  /** Lifetime of the access token in seconds. */
  expires_in: z.number().int().positive(),
  /** Single use: every refresh returns a new one. */
  refresh_token: z.string().min(1),
})
export type TokenPair = z.infer<typeof tokenPairSchema>

export const loginResponseSchema = tokenPairSchema.extend({ user: authUserSchema })

export const refreshRequestSchema = z.object({
  refresh_token: z.string().min(1),
})
export type RefreshRequest = z.infer<typeof refreshRequestSchema>

/** Empty input fails with `too_small`, malformed input with `invalid_format`. */
export const emailSchema = z.string().trim().min(1).max(255).pipe(z.email())

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1),
})
export type LoginRequest = z.infer<typeof loginRequestSchema>
